// Excel (.xlsx) armado aqui, sin librerias (D95): una hoja por tabla, encabezado con el color XmarteK (1F4E5F, letra
// blanca), fila de encabezado fija y con filtro, ancho de columnas segun el contenido. Los numeros van como numeros y
// las fechas como fechas de Excel, para poder sumar, filtrar y ordenar. Funciones puras salvo la descarga.
import { bajarArchivo } from './documentoXmartek';
import { zip } from './zip';

export type Celda = string | number | boolean | Date | null | undefined;
export type Hoja = { nombre: string; filas: Celda[][] };

export const TIPO_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const esc = (s: string) =>
  s
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f￾￿]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** A, B, …, Z, AA, AB… */
export function letra(i: number) {
  let s = '';
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}

/** Fecha de Excel (dias desde 1899-12-30) con la hora local. */
export const serialExcel = (d: Date) => (d.getTime() - d.getTimezoneOffset() * 60_000) / 86_400_000 + 25569;

/** Nombre valido de hoja: maximo 31 caracteres, sin []:*?/\ y sin repetir. */
export function nombresHojas(nombres: string[]) {
  const usados = new Set<string>();
  return nombres.map((n) => {
    const base = (n.replace(/[[\]:*?/\\]/g, ' ').replace(/^'+|'+$/g, '').trim() || 'Hoja').slice(0, 31);
    let x = base;
    for (let k = 2; usados.has(x.toLowerCase()); k++) x = `${base.slice(0, 31 - String(k).length - 1)} ${k}`;
    usados.add(x.toLowerCase());
    return x;
  });
}

// Estilos: 0 normal, 1 encabezado, 2 fecha, 3 fecha y hora, 4 numero con 2 decimales.
const ESTILOS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><numFmts count="2"><numFmt numFmtId="164" formatCode="dd/mm/yyyy"/><numFmt numFmtId="165" formatCode="dd/mm/yyyy hh:mm"/></numFmts><fonts count="2"><font><sz val="11"/><name val="Calibri"/><family val="2"/></font><font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/><family val="2"/></font></fonts><fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FF1F4E5F"/><bgColor indexed="64"/></patternFill></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="5"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf><xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/><xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/><xf numFmtId="4" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`;

function celdaXml(v: Celda, ref: string, encabezado: boolean) {
  if (v == null || v === '' || (typeof v === 'number' && !Number.isFinite(v))) return encabezado ? `<c r="${ref}" s="1"/>` : '';
  const s = encabezado ? ' s="1"' : '';
  if (typeof v === 'number' && !encabezado) return `<c r="${ref}"${Number.isInteger(v) ? '' : ' s="4"'}><v>${v}</v></c>`;
  if (typeof v === 'boolean' && !encabezado) return `<c r="${ref}" t="b"><v>${v ? 1 : 0}</v></c>`;
  if (v instanceof Date && !encabezado) {
    if (Number.isNaN(v.getTime())) return '';
    const conHora = v.getHours() || v.getMinutes();
    return `<c r="${ref}" s="${conHora ? 3 : 2}"><v>${serialExcel(v)}</v></c>`;
  }
  const t = esc(String(v instanceof Date ? v.toISOString() : v).slice(0, 32_000));
  return `<c r="${ref}" t="inlineStr"${s}><is><t xml:space="preserve">${t}</t></is></c>`;
}

/** Ancho por columna (en caracteres) segun el texto mas largo, entre 8 y 60. */
export function anchos(filas: Celda[][]) {
  const n = Math.max(0, ...filas.map((f) => f.length));
  return Array.from({ length: n }, (_, i) =>
    Math.min(60, Math.max(8, ...filas.map((f) => {
      const v = f[i];
      const largo = v instanceof Date ? 16 : typeof v === 'number' ? String(Math.round(v * 100) / 100).length + 2 : String(v ?? '').split('\n').reduce((m, l) => Math.max(m, l.length), 0);
      return largo + 2;
    }))),
  );
}

export function hojaXml(filas: Celda[][]) {
  const cols = anchos(filas);
  const ultima = filas.length ? `${letra(Math.max(0, cols.length - 1))}${filas.length}` : 'A1';
  const fijar = filas.length > 1 ? '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>' : '<sheetViews><sheetView workbookViewId="0"/></sheetViews>';
  const colsXml = cols.length ? `<cols>${cols.map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`).join('')}</cols>` : '';
  const datos = filas.map((f, r) => `<row r="${r + 1}">${f.map((v, c) => celdaXml(v, `${letra(c)}${r + 1}`, r === 0)).join('')}</row>`).join('');
  const filtro = filas.length > 1 && cols.length ? `<autoFilter ref="A1:${ultima}"/>` : '';
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><dimension ref="A1:${ultima}"/>${fijar}<sheetFormatPr defaultRowHeight="15"/>${colsXml}<sheetData>${datos}</sheetData>${filtro}</worksheet>`;
}

/** Las partes del .xlsx (para el zip). La primera fila de cada hoja es el encabezado. */
export function partesXlsx(hojas: Hoja[]): [string, string][] {
  const lista = hojas.length ? hojas : [{ nombre: 'Hoja', filas: [] }];
  const nombres = nombresHojas(lista.map((h) => h.nombre));
  const filtros = lista
    .map((h, i) => (h.filas.length > 1 ? `<definedName name="_xlnm._FilterDatabase" localSheetId="${i}" hidden="1">'${esc(nombres[i].replace(/'/g, "''"))}'!$A$1:$${letra(Math.max(0, anchos(h.filas).length - 1))}$${h.filas.length}</definedName>` : ''))
    .join('');
  return [
    [
      '[Content_Types].xml',
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>${lista.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')}</Types>`,
    ],
    [
      '_rels/.rels',
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
    ],
    [
      'xl/workbook.xml',
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>${nombres.map((n, i) => `<sheet name="${esc(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')}</sheets>${filtros ? `<definedNames>${filtros}</definedNames>` : ''}</workbook>`,
    ],
    [
      'xl/_rels/workbook.xml.rels',
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${lista.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('')}<Relationship Id="rId${lista.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`,
    ],
    ['xl/styles.xml', ESTILOS],
    ...lista.map((h, i): [string, string] => [`xl/worksheets/sheet${i + 1}.xml`, hojaXml(h.filas)]),
  ];
}

export const libroXlsx = (hojas: Hoja[]) => zip(partesXlsx(hojas));

export function descargarXlsx(nombre: string, hojas: Hoja[]) {
  const archivo = nombre.toLowerCase().endsWith('.xlsx') ? nombre : `${nombre}.xlsx`;
  bajarArchivo(new Blob([libroXlsx(hojas)], { type: TIPO_XLSX }), archivo);
}
